import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, Clock, Play, Users, History, Settings, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayoutSchedulerSettings = () => {
  const [config, setConfig] = useState({
    enabled: false,
    day_of_month: 1,
    min_payout_amount: 50,
    payment_method: 'bank_transfer',
    send_notifications: true
  });
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, previewRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/payout-schedule/config`, { headers }),
        axios.get(`${API_URL}/api/payout-schedule/preview`, { headers }),
        axios.get(`${API_URL}/api/payout-schedule/history`, { headers })
      ]);
      
      setConfig(configRes.data);
      setPreview(previewRes.data);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Failed to fetch payout schedule data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/payout-schedule/config`, config, { headers });
      toast.success(`Payout schedule ${config.enabled ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!window.confirm('This will process all eligible payouts immediately. Continue?')) return;
    
    setRunningNow(true);
    try {
      const res = await axios.post(`${API_URL}/api/payout-schedule/run-now`, {}, { headers });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run payout cycle');
    } finally {
      setRunningNow(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Automatic Payout Scheduler</h2>
          <p className="text-sm text-gray-500">Configure monthly automatic payouts for affiliates</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            config.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {config.enabled ? 'Active' : 'Disabled'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {['settings', 'preview', 'history'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab 
                ? 'border-[#003456] text-[#003456]' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'settings' && <Settings className="h-4 w-4 inline mr-2" />}
            {tab === 'preview' && <Users className="h-4 w-4 inline mr-2" />}
            {tab === 'history' && <History className="h-4 w-4 inline mr-2" />}
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="font-medium">Enable Automatic Payouts</p>
              <p className="text-sm text-gray-500">Automatically process payouts on a monthly schedule</p>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-2" />
                Day of Month
              </label>
              <select
                value={config.day_of_month}
                onChange={(e) => setConfig({ ...config, day_of_month: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                disabled={!config.enabled}
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>
                    {day === 1 ? '1st' : day === 2 ? '2nd' : day === 3 ? '3rd' : `${day}th`} of each month
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Payouts run at 9:00 AM UTC</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="h-4 w-4 inline mr-2" />
                Minimum Payout Amount
              </label>
              <Input
                type="number"
                value={config.min_payout_amount}
                onChange={(e) => setConfig({ ...config, min_payout_amount: parseFloat(e.target.value) })}
                min={0}
                step={10}
                disabled={!config.enabled}
              />
              <p className="text-xs text-gray-500 mt-1">Affiliates below this threshold won't receive payouts</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              <select
                value={config.payment_method}
                onChange={(e) => setConfig({ ...config, payment_method: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                disabled={!config.enabled}
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="paypal">PayPal</option>
                <option value="check">Check</option>
              </select>
            </div>

            <div className="flex items-center">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.send_notifications}
                  onChange={(e) => setConfig({ ...config, send_notifications: e.target.checked })}
                  className="rounded border-gray-300"
                  disabled={!config.enabled}
                />
                <div>
                  <p className="font-medium text-sm">Send Email Notifications</p>
                  <p className="text-xs text-gray-500">Notify affiliates when payouts are processed</p>
                </div>
              </label>
            </div>
          </div>

          {config.enabled && config.next_scheduled_run && (
            <div className="bg-blue-50 rounded-lg p-4 flex items-center gap-3">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-800">Next Scheduled Run</p>
                <p className="text-sm text-blue-600">
                  {new Date(config.next_scheduled_run).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Settings
            </Button>
            <Button 
              variant="outline" 
              onClick={handleRunNow}
              disabled={runningNow}
              className="text-green-600 border-green-200 hover:bg-green-50"
            >
              {runningNow ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
              ) : (
                <><Play className="h-4 w-4 mr-2" /> Run Payout Cycle Now</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold">Next Payout Preview</h3>
              <p className="text-sm text-gray-500">Affiliates eligible for payout (cleared 30-day window, above ${config.min_payout_amount} minimum)</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}>
              Refresh
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-[#003456]">{preview?.eligible_affiliates || 0}</p>
              <p className="text-sm text-gray-500">Eligible Affiliates</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-600">${(preview?.total_amount || 0).toFixed(2)}</p>
              <p className="text-sm text-gray-500">Total to Pay</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-gray-600">${preview?.min_payout_threshold || 50}</p>
              <p className="text-sm text-gray-500">Minimum Threshold</p>
            </div>
          </div>

          {preview?.affiliates?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 font-medium">Affiliate</th>
                    <th className="text-left p-3 font-medium">Sales</th>
                    <th className="text-left p-3 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.affiliates.map((aff, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-3">
                        <p className="font-medium">{aff.affiliate_name}</p>
                        <p className="text-xs text-gray-500">{aff.affiliate_email}</p>
                      </td>
                      <td className="p-3">{aff.sales_count}</td>
                      <td className="p-3 font-medium text-green-600">${aff.amount_to_pay?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No affiliates currently eligible for payout</p>
              <p className="text-sm">Commissions must clear the 30-day window and meet the minimum threshold</p>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold mb-4">Payout History</h3>
          
          {history.length > 0 ? (
            <div className="space-y-4">
              {history.map((run, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-medium">
                        {new Date(run.run_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {run.result?.affiliates_paid || 0} affiliates paid
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded p-3 text-sm">
                    <p><strong>Total Paid:</strong> ${(run.result?.total_amount_paid || 0).toFixed(2)}</p>
                    <p className="text-gray-500">{run.result?.message}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No payout history yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PayoutSchedulerSettings;
